import { Component, OnInit, AfterViewInit, OnDestroy, ViewChild, ElementRef, NgZone } from '@angular/core';
import { Router } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';

import { AuthService } from '../../services/auth.service';
import { LoadingService } from '../../../../../core/services/loading.service';
import { NotificationService } from '../../../../../core/services/notification.service';
import { SharedModule } from '../../../../../shared/shared.module';
import { TwoFactorComponent } from '../../../user-management/components/two-factor/two-factor.component';
import { SessionService } from '../../../user-management/services/session.service';

interface Ember {
  x: number;
  y: number;
  size: number;
  speedY: number;
  speedX: number;
  alpha: number;
  maxAlpha: number;
  color: string;
}

@Component({
  selector: 'app-login',
  standalone: true,
  imports: [SharedModule],
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.scss'],
})
export class LoginComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('containerRef', { static: false }) containerRef!: ElementRef<HTMLDivElement>;
  @ViewChild('atmosphereCanvas', { static: false }) atmosphereCanvasRef!: ElementRef<HTMLCanvasElement>;

  username = '';
  password = '';
  hidePassword = true;

  private animationFrameId: number | null = null;
  private resizeHandler: (() => void) | null = null;
  private mouseMoveHandler: ((e: MouseEvent) => void) | null = null;

  constructor(
    private readonly notificationService: NotificationService,
    private readonly authService: AuthService,
    public readonly loadingService: LoadingService,
    private readonly router: Router,
    private readonly sessionService: SessionService,
    private readonly dialog: MatDialog,
    private readonly ngZone: NgZone
  ) { }

  ngOnInit(): void {
    if (this.authService.isAuthenticated()) {
      this.notificationService.showInfo('Ya estás logueado');
      this.router.navigate(['/home']);
    }
  }

  ngAfterViewInit(): void {
    this.initInteractiveLighting();
    this.initAtmosphereCanvas();
  }

  ngOnDestroy(): void {
    if (this.animationFrameId !== null) {
      cancelAnimationFrame(this.animationFrameId);
    }
    if (this.resizeHandler) {
      window.removeEventListener('resize', this.resizeHandler);
    }
    if (this.mouseMoveHandler) {
      window.removeEventListener('mousemove', this.mouseMoveHandler);
    }
  }

  /**
   * Resplandor interactivo de luz tenue carmesí/carbón que sigue al cursor
   */
  private initInteractiveLighting(): void {
    const container = this.containerRef?.nativeElement;
    if (!container) return;

    this.ngZone.runOutsideAngular(() => {
      this.mouseMoveHandler = (e: MouseEvent) => {
        const rect = container.getBoundingClientRect();
        const x = e.clientX - rect.left;
        const y = e.clientY - rect.top;
        container.style.setProperty('--mouse-x', `${x}px`);
        container.style.setProperty('--mouse-y', `${y}px`);
      };

      window.addEventListener('mousemove', this.mouseMoveHandler, { passive: true });
    });
  }

  /**
   * Atmósfera Sumi-e: brasas y partículas de tinta sutiles flotando hacia arriba (30 FPS cinematográfico)
   */
  private initAtmosphereCanvas(): void {
    const canvas = this.atmosphereCanvasRef?.nativeElement;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    this.ngZone.runOutsideAngular(() => {
      let embers: Ember[] = [];
      const emberCount = 38;
      const colors = ['#e11d48', '#f43f5e', '#fb7185', '#ffffff'];

      const setCanvasSize = () => {
        const parent = canvas.parentElement;
        if (!parent) return;
        canvas.width = parent.clientWidth;
        canvas.height = parent.clientHeight;

        embers = [];
        for (let i = 0; i < emberCount; i++) {
          embers.push(createEmber(true));
        }
      };

      const createEmber = (randomY = false): Ember => {
        return {
          x: Math.random() * canvas.width,
          y: randomY ? Math.random() * canvas.height : canvas.height + Math.random() * 20,
          size: 1 + Math.random() * 2.2,
          speedY: 0.4 + Math.random() * 0.7,
          speedX: (Math.random() - 0.5) * 0.4,
          alpha: 0.1 + Math.random() * 0.4,
          maxAlpha: 0.3 + Math.random() * 0.5,
          color: colors[Math.floor(Math.random() * colors.length)]
        };
      };

      setCanvasSize();
      this.resizeHandler = () => setCanvasSize();
      window.addEventListener('resize', this.resizeHandler);

      let lastTime = 0;
      const fpsInterval = 1000 / 30; // 30 FPS suave

      const render = (currentTime: number) => {
        this.animationFrameId = requestAnimationFrame(render);

        const elapsed = currentTime - lastTime;
        if (elapsed < fpsInterval) return;
        lastTime = currentTime - (elapsed % fpsInterval);

        ctx.clearRect(0, 0, canvas.width, canvas.height);

        for (let i = 0; i < embers.length; i++) {
          const e = embers[i];
          e.y -= e.speedY;
          e.x += e.speedX;

          // Parpadeo suave
          ctx.beginPath();
          ctx.arc(e.x, e.y, e.size, 0, Math.PI * 2);
          ctx.fillStyle = e.color;
          ctx.globalAlpha = e.alpha;
          ctx.shadowBlur = 8;
          ctx.shadowColor = e.color;
          ctx.fill();
          ctx.shadowBlur = 0;

          // Si sale de la pantalla superior, renace abajo
          if (e.y < -10 || e.x < -10 || e.x > canvas.width + 10) {
            embers[i] = createEmber(false);
          }
        }
        ctx.globalAlpha = 1;
      };

      this.animationFrameId = requestAnimationFrame(render);
    });
  }

  onSubmit(): void {
    this.loadingService.show();

    this.authService.login({ username: this.username, password: this.password }).subscribe({
      next: (response: any) => {
        this.loadingService.hide();

        if (response.requires2FA) {
          this.notificationService.showInfo('Este usuario requiere autenticación 2FA');

          const dialogRef = this.dialog.open(TwoFactorComponent, {
            disableClose: true,
            data: { username: response.username || this.username, modo: 'login' },
          });

          dialogRef.afterClosed().subscribe((result) => {
            if (result === 'success') {
              this.sessionService.startSessionTimer();
              this.router.navigate(['/home']).then(() => {
                this.notificationService.showSuccess('Sesión iniciada con 2FA ✅');
              });
            } else {
              this.notificationService.showInfo('Debe completar la verificación 2FA.');
            }
          });

          return;
        }

        if (response.mustSetup2FA) {
          this.notificationService.showInfo('Debe configurar la autenticación 2FA antes de continuar.');

          const dialogRef = this.dialog.open(TwoFactorComponent, {
            disableClose: true,
            data: { username: response.username || this.username, modo: 'setup' },
          });

          dialogRef.afterClosed().subscribe((result) => {
            if (result === 'success' || result === 'activado') {
              this.notificationService.showSuccess('2FA activado correctamente ✅');
              this.router.navigate(['nino-shopping/stats']);
            } else {
              this.notificationService.showInfo('Debe activar el 2FA para continuar.');
            }
          });

          return;
        }

        if (response.token) {
          this.authService.setToken(response.token);
          this.sessionService.startSessionTimer();

          this.router.navigate(['/home']).then(() => {
            this.loadingService.hide();
            this.notificationService.showSuccess('Sesión Iniciada ✅');
          });
        } else {
          this.notificationService.showError(response.message || 'Error en el login');
        }
      },

      error: (error) => {
        this.loadingService.hide();
        this.notificationService.showError('Error al iniciar sesión');
        console.error('Error al iniciar sesión', error);
      },
    });
  }
}
